import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#000000", color: "rgba(255,255,255,0.9)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14,
            color: "rgba(255,255,255,0.4)",
            textDecoration: "none",
            marginBottom: 40,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "rgba(255,255,255,0.9)")}
          onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
        >
          <ArrowLeft size={14} /> Back to homepage
        </Link>

        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6, color: "#ffffff" }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 16 }}>
          Last updated: 1 July 2026
        </p>

        {/* TODO (post sole-trader registration): replace with the registered trading name
            and a correspondence address. Under UK GDPR the data controller's identity and
            contact details must be provided. */}
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 48, lineHeight: 1.6 }}>
          The data controller for Margin Metrics is its founder, operating as a sole trader
          based in the United Kingdom. For the full trading name, a correspondence address,
          or any data-protection request, contact{" "}
          <a href="mailto:margin.store26@gmail.com" style={{ color: "#1d9e75" }}>
            margin.store26@gmail.com
          </a>.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Section title="1. Data We Collect">
            <p>
              <Strong>Account information:</Strong> When you sign in via Google, we receive your email address
              and display name. We do not receive your Google password or access to other Google services.
            </p>
            <p>
              <Strong>Trade data:</Strong> All trade entries, journal notes, strategy tags, and performance
              data you enter manually into the platform. This data belongs entirely to you.
            </p>
            <p>
              <Strong>Usage data:</Strong> Anonymous interaction data such as pages visited and features used,
              collected solely to improve the platform. No personally identifiable usage data is retained.
            </p>
          </Section>

          <Section title="2. How We Store Your Data">
            <p>
              All data is stored in <Strong>Supabase</Strong>, a secure cloud database provider. Data is
              encrypted at rest using AES-256 and in transit using TLS 1.2+. Row-level security (RLS)
              policies ensure you can only access your own data. Access to production data is strictly
              limited to the platform operator.
            </p>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>
              Your data is used to provide the platform&apos;s features — trade analytics, AI coaching,
              risk monitoring, and performance reporting. We do not use your data for advertising or sell
              it to any third party for any purpose.
            </p>
          </Section>

          <Section title="4. Data Sharing">
            <p>
              We do <Strong>not</Strong> sell, rent, or share your personal data with third parties for
              marketing purposes. Data may only be shared with:
            </p>
            <ul>
              <li><Strong>Supabase</Strong> — cloud database &amp; authentication hosting; stores all your account and trade data (EU/UK region).</li>
              <li><Strong>Stripe</Strong> — subscription billing and payment processing; limited to billing data only.</li>
              <li><Strong>Anthropic</Strong> — AI providers that generate coaching and analytics responses; your trade data is included in prompts but is not retained for training.</li>
              <li><Strong>Resend</Strong> — transactional email delivery (e.g. alerts); receives your email address only.</li>
              <li><Strong>Cloudflare</Strong> — content delivery and media hosting; may process your IP address to serve the app.</li>
              <li><Strong>Reddit &amp; Meta</Strong> — advertising/analytics pixels used to measure marketing; <Strong>only loaded if you accept</Strong> analytics cookies in the cookie banner. Reject and no data is shared with them.</li>
              <li>Law enforcement, only when required by applicable law.</li>
            </ul>
            <p>
              Market-data providers (e.g. Databento, Yahoo Finance) receive only instrument symbols — never
              your personal data. We do not sell, rent, or share your personal data for marketing purposes.
            </p>
          </Section>

          <Section title="5. Your Rights">
            <p>You have the right to:</p>
            <ul>
              <li>Access all data we hold about you.</li>
              <li>Export your trade data at any time from Settings.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Delete your account and all associated data.</li>
            </ul>
            <p>
              To request data deletion or export, email us at{" "}
              <a
                href="mailto:margin.store26@gmail.com"
                style={{ color: "#1d9e75", textDecoration: "none" }}
              >
                margin.store26@gmail.com
              </a>
              . Deletion requests are fulfilled within 30 days.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              Your data is retained for as long as your account is active. When you delete your account,
              all personal data is permanently removed within 30 days. Aggregated, anonymised analytics
              may be retained indefinitely.
            </p>
          </Section>

          <Section title="7. Children">
            <p>
              Margin Metrics is not intended for users under 18. We do not knowingly collect data from
              minors. If you believe a minor has created an account, contact us at the email address above.
            </p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>
              We may update this policy from time to time. Material changes will be communicated via
              in-app notice or email at least 14 days before taking effect. Continued use of the platform
              after that date constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="9. Contact">
            <p>
              For any privacy-related questions, contact us at{" "}
              <a
                href="mailto:margin.store26@gmail.com"
                style={{ color: "#1d9e75", textDecoration: "none" }}
              >
                margin.store26@gmail.com
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 28, paddingBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: "#ffffff", marginBottom: 14 }}>{title}</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          fontSize: 14,
          lineHeight: 1.7,
          color: "rgba(255,255,255,0.5)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>{children}</strong>;
}
